import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, Download } from "lucide-react";
import { ParticleCanvas } from "./ParticleCanvas";
import { scrollToId } from "@/lib/lenis";
import { profile, heroMetrics } from "@/data/portfolio";

const RevealLine = ({ children, delay, className }) => (
  <span className={`block overflow-hidden ${className || ""}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 20 });
  const sy = useSpring(my, { stiffness: 50, damping: 20 });
  const lx = useMotionValue(-400);
  const ly = useMotionValue(-400);
  const lensX = useSpring(lx, { stiffness: 120, damping: 18 });
  const lensY = useSpring(ly, { stiffness: 120, damping: 18 });

  const onMouse = (e) => {
    mx.set((e.clientX / window.innerWidth - 0.5) * 40);
    my.set((e.clientY / window.innerHeight - 0.5) * 40);
    const rect = ref.current?.getBoundingClientRect();
    if (rect) {
      lx.set(e.clientX - rect.left - 90);
      ly.set(e.clientY - rect.top - 90);
    }
  };

  return (
    <section
      id="home"
      ref={ref}
      onMouseMove={onMouse}
      className="relative flex min-h-screen flex-col justify-center overflow-hidden"
      data-testid="hero-section"
    >
      <ParticleCanvas />
      <motion.div
        style={{ x: sx, y: sy }}
        className="pointer-events-none absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full orb-violet-strong"
      />
      <motion.div
        style={{ x: sx, y: sy }}
        className="pointer-events-none absolute -right-40 bottom-1/4 h-[420px] w-[420px] rounded-full orb-cyan-strong"
      />
      <div className="animate-blob pointer-events-none absolute left-1/2 top-1/2 h-[560px] w-[560px] orb-blob" />
      <motion.div
        style={{ x: lensX, y: lensY }}
        className="cursor-lens pointer-events-none absolute left-0 top-0 z-20 hidden h-[180px] w-[180px] rounded-full border border-cyan-300/30 lg:block"
        data-testid="hero-lens"
      />

      <motion.div style={{ y, opacity }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pt-28 sm:px-8 lg:px-16">
        <RevealLine delay={0.35}>
          <span className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-400" data-testid="hero-eyebrow">
            {"// "}{profile.name} — Portfolio 2026
          </span>
        </RevealLine>

        <h1 className="mt-6 font-display text-5xl font-extrabold leading-[1.02] tracking-tight text-slate-50 sm:text-7xl lg:text-8xl">
          <RevealLine delay={0.5}>DATA</RevealLine>
          <RevealLine delay={0.65}>
            <span className="gradient-text">SCIENTIST</span>
          </RevealLine>
          <RevealLine delay={0.8}>
            <span className="text-outline">&amp; QUANT</span>
          </RevealLine>
          <RevealLine delay={0.95}>ENGINEER</RevealLine>
        </h1>

        <RevealLine delay={1.15} className="mt-8 max-w-xl">
          <p className="text-base leading-relaxed text-slate-400 sm:text-lg" data-testid="hero-tagline">
            {profile.tagline}
          </p>
        </RevealLine>

        <RevealLine delay={1.3}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-explore-projects-button"
              onClick={() => scrollToId("#projects")}
              className="group flex items-center gap-3 rounded-full bg-violet-600 px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-widest text-white transition-all duration-300 hover:bg-violet-500 hover:glow-violet"
            >
              Explore Projects
              <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <a
              data-testid="hero-download-cv-button"
              href={profile.cvUrl}
              download
              className="flex items-center gap-3 rounded-full border border-white/15 px-7 py-3.5 font-mono text-xs uppercase tracking-widest text-slate-300 transition-all duration-300 hover:border-cyan-400/60 hover:text-cyan-300 hover:glow-cyan"
            >
              <Download size={15} /> Download CV
            </a>
          </div>
        </RevealLine>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.55, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 grid max-w-2xl grid-cols-2 gap-6 border-t border-white/10 pt-8 sm:grid-cols-4"
          data-testid="hero-metrics"
        >
          {heroMetrics.map((m) => (
            <div key={m.label}>
              <div className="font-display text-3xl font-bold text-slate-50">{m.value}</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{m.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollToId("#projects")}
        data-testid="hero-spin-badge"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2.2, type: "spring", stiffness: 120, damping: 14 }}
        whileHover={{ scale: 1.08 }}
        className="group absolute bottom-16 right-8 z-10 hidden h-28 w-28 items-center justify-center lg:flex"
        aria-label="Scroll to projects"
      >
        <svg viewBox="0 0 100 100" className="animate-spin-slow absolute inset-0 h-full w-full">
          <defs>
            <path id="badge-circle" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
          </defs>
          <text className="fill-slate-400 font-mono text-[8px] uppercase tracking-[2px]">
            <textPath href="#badge-circle">open to opportunities ✦ data × quant × ai ✦</textPath>
          </text>
        </svg>
        <span className="flex h-11 w-11 items-center justify-center rounded-full border border-violet-500/40 bg-violet-500/10 text-violet-300 transition-all duration-300 group-hover:bg-violet-500/25 group-hover:glow-violet">
          <ArrowDown size={16} />
        </span>
      </motion.button>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-600"
        >
          scroll
        </motion.div>
      </motion.div>
    </section>
  );
};
